import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import type { ScatterPlotCommonProps, ScatterPlotDatum, ScatterPlotRawSerie } from "@nivo/scatterplot"
import { ResponsiveScatterPlot } from "@nivo/scatterplot"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import { customColorScheme } from "../../../utils/genericHelpers"

export type BaseScatterPlotProps = {
  data: ScatterPlotRawSerie<{
    x: number
    y: string
    bin: string
    decision?: string
    reason?: string
  }>[]
  margin: Partial<{ top: number; right: number; bottom: number; left: number }>
  axisTop?: ScatterPlotCommonProps<ScatterPlotDatum>["axisTop"]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderTickFn?: (props: any) => JSX.Element
}

export const BaseScatterPlot = ({ data, margin, axisTop, renderTickFn }: BaseScatterPlotProps): ReactElement => {
  const theme = getTheme()
  const { t } = useTranslation("graphs")

  return (
    <ResponsiveScatterPlot
      data={data}
      useMesh={false}
      theme={NivoTheme(theme)}
      colors={customColorScheme()}
      margin={margin}
      xScale={{ type: "linear", min: -1, max: 1 }}
      xFormat=">-.2f"
      yScale={{ type: "point" }}
      axisTop={axisTop}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 15,
        tickRotation: 0,
        legendOffset: 45,
      }}
      axisLeft={{
        tickSize: 6,
        tickPadding: 15,
        tickRotation: 0,
        legendOffset: -85,
        renderTick: renderTickFn,
      }}
      tooltip={(cell) => {
        return (
          <div className={"chart-tooltip"}>
            <Grid item mb={1} display={"flex"}>
              <span
                key="chip"
                style={{
                  display: "block",
                  width: "12px",
                  height: "12px",
                  background: cell.node.color,
                  marginRight: "8px",
                }}
              />

              <Typography variant="label-bold" variantColor={2}>
                {cell.node.yValue}
              </Typography>
            </Grid>

            <Typography variant="label-bold" variantColor={2}>
              {t("scatterPlot.tooltip.correlation")}{" "}
              {(typeof cell.node.data.x === "number" ? cell.node.data.x : parseFloat(cell.node.data.x)).toFixed(3)}
            </Typography>

            <Typography variant="label-bold" variantColor={2}>
              {t("scatterPlot.tooltip.value")} {cell.node.data.bin ?? t("notAvailable", { ns: "common" })}
            </Typography>

            {cell.node.data.decision && (
              <Typography variant="label-bold" variantColor={2}>
                {t("scatterPlot.tooltip.decision")} {cell.node.data.decision ?? ""}
              </Typography>
            )}

            {cell.node.data.reason && (
              <Typography variant="label-bold" variantColor={2}>
                {t("scatterPlot.tooltip.reason")} {cell.node.data.reason ?? ""}
              </Typography>
            )}
          </div>
        )
      }}
    />
  )
}
